import React, { useState, useEffect } from "react";
import CommonButton from "../../../components/CommonButton";
import { useLazyGetFaqActiveQuery } from "../../../redux/actions/Customer/FooterCustomer";
import { GetAllFaqData } from "../../../types/Response/CustomerResponse";
import { useNavigate } from "react-router-dom";

export const CustomerFAQ: React.FC = () => {
  const navigate = useNavigate();

  const [triggerGetActiveFaq, { data: faqApiResponse, isLoading, isError }] =
    useLazyGetFaqActiveQuery();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number>(1);
  const [expandedFaqId, setExpandedFaqId] = useState<number | null>(null);

  useEffect(() => {
    triggerGetActiveFaq(selectedCategory);
  }, [selectedCategory, triggerGetActiveFaq]);

  const faqs: GetAllFaqData[] = faqApiResponse?.value?.data || [];

  const toggleAccordion = (id: number) => {
    setExpandedFaqId((prevId) => (prevId === id ? null : id));
  };

  const categories = [
    { id: 1, name: "General" },
    { id: 2, name: "Services" },
    { id: 3, name: "Pets" },
    { id: 4, name: "Products" },
    { id: 5, name: "Bookings" },
    { id: 6, name: "Payments" },
    { id: 7, name: "Delivery" },
  ];

  const filteredFaqs = faqs.filter((faq) => {
    return (
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div className="p-4 min-h-screen bg-gray-50 text-gray-900 w-full max-w-full overflow-x-hidden box-border">
      <div className="max-w-4xl mx-auto w-full py-8 sm:py-12">
        {/* HEADER */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            Frequently Asked Questions
          </h1>
          <p className="mt-3 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Need help with your account, billing setup, or configuration
            parameters? Find instant steps prepared by our help center team.
          </p>
        </div>

        {/* SEARCH BOX INPUT */}
        <div className="mb-8 max-w-xl mx-auto">
          <div className="relative rounded-md shadow-sm">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="w-full p-3.5 pl-4 pr-12 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none box-border shadow-inner transition-all"
            />
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-400">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* FILTER NAVIGATION BUTTONS */}
        <div className="flex flex-wrap justify-center items-center gap-2 mb-10 w-full">
          {categories.map((category) => (
            <CommonButton
              key={category.id}
              onClick={() => {
                setSelectedCategory(category.id);
                setExpandedFaqId(null);
              }}
              text={category.name}
              variant={selectedCategory === category.id ? "filled" : "outline"}
              rounded="full"
              className="text-xs sm:text-sm tracking-wide"
            />
          ))}
        </div>

        {/* INTERACTIVE ACCORDION ROW LIST */}
        <div className="space-y-4 w-full">
          {isLoading ? (
            <div className="text-center py-12 text-gray-400 font-medium text-sm">
              Fetching active queries assistance indices...
            </div>
          ) : isError ? (
            <div className="text-center py-12 text-red-500 font-medium text-sm">
              Failed to establish a line with help-desk data configurations.
            </div>
          ) : filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => {
              const isExpanded = expandedFaqId === faq.id;
              return (
                <div
                  key={faq.id}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:border-gray-300 w-full box-border"
                >
                  {/* Header Trigger */}
                  <button
                    type="button"
                    onClick={() => toggleAccordion(faq.id)}
                    className="w-full text-left p-5 flex justify-between items-center gap-4 bg-white hover:bg-gray-50/50 transition-colors focus:outline-none"
                  >
                    <div className="space-y-1.5 flex-1">
                      <span className="inline-block bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border border-indigo-100">
                        {faq.categoryName || selectedCategory}
                      </span>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base leading-snug break-words">
                        {faq.question}
                      </h3>
                    </div>

                    {/* Expand/Collapse Chevron Indicator */}
                    <div
                      className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                        isExpanded ? "transform rotate-180 text-blue-600" : ""
                      }`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>

                  {/* Collapsible Content Area */}
                  <div
                    className={`transition-all duration-200 ease-in-out overflow-hidden ${
                      isExpanded
                        ? "max-h-[1000px] border-t border-gray-100"
                        : "max-h-0"
                    }`}
                  >
                    <div className="p-5 text-gray-600 text-sm sm:text-base leading-relaxed break-words bg-gray-50/20">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            /* EMPTY SEARCH DISCOVERY FALLBACK */
            <div className="text-center py-16 bg-white border border-gray-200 rounded-lg shadow-sm w-full box-border">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900 tracking-tight">
                No match discovered
              </h3>
            </div>
          )}
        </div>

        {/* FOOTER CALL TO ACTION */}
        <div className="mt-12 text-center border-t border-gray-200 pt-8 w-full">
          <p className="text-sm text-gray-600 mb-4">
            Still looking for clarification or need direct help desk support?
          </p>
          <div className="flex justify-center w-full">
            <CommonButton
              text="Contact Support Help Desk"
              variant="outline"
              rounded="md"
              onClick={() => {
                navigate("/contactus");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};